// src/components/commissioner/MinorLeaguePicksOverride.js
// Mirrors DraftPicksOverride.js exactly — same columns, same layout, same modal
import React, { useState, useEffect } from 'react';
import { AlertCircle, Check, ArrowLeftRight, X } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';

const MinorLeaguePicksOverride = ({ leagueId, league, teams = [] }) => {
  const [allPicks, setAllPicks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [reassigning, setReassigning] = useState(null);
  const [targetTeamId, setTargetTeamId] = useState('');
  const [draftStatus, setDraftStatus] = useState(null);

  const displayYear = league?.draft_year;

  useEffect(() => {
    if (displayYear) loadPicks();
  }, [leagueId, displayYear]);

  const loadPicks = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await leaguesAPI.getMinorLeagueDraft(leagueId, displayYear);
      setAllPicks(result?.resolved_pick_grid || []);
      setDraftStatus(result?.draft_status || null);
    } catch (err) {
      if (err.response?.status === 404) {
        setAllPicks([]);
      } else {
        setError('Failed to load ML draft picks');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredPicks = selectedTeamId
    ? allPicks.filter(pick =>
        pick.current_owner_team_id === selectedTeamId ||
        pick.team_id === selectedTeamId
      )
    : allPicks;

  const handleReassign = async () => {
    if (!reassigning || !targetTeamId) return;
    setReassigning(prev => ({ ...prev, saving: true }));
    setError('');
    try {
      await leaguesAPI.reassignMLPickSlot(
        leagueId,
        reassigning.round_number,
        reassigning.pick_in_round,
        targetTeamId,
        displayYear
      );
      setMessage(
        `Round ${reassigning.round_number} pick reassigned to ${
          teams.find(t => t.team_id === targetTeamId)?.team_name || targetTeamId
        }`
      );
      setTimeout(() => setMessage(''), 5000);
      setReassigning(null);
      setTargetTeamId('');
      await loadPicks();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reassign pick');
      setReassigning(prev => ({ ...prev, saving: false }));
    }
  };

  const openReassignModal = (pick) => {
    setReassigning(pick);
    setTargetTeamId('');
  };

  const closeReassignModal = () => {
    setReassigning(null);
    setTargetTeamId('');
  };

  if (!displayYear) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-8 text-center`}>
        <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${dynastyTheme.classes.text.warning}`} />
        <p className={dynastyTheme.classes.text.neutralLight}>League draft_year not set.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {message && (
        <div className={`p-3 rounded-lg border ${dynastyTheme.classes.border.neutral} bg-green-500/10 text-green-400 flex items-center gap-3`}>
          <Check className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{message}</span>
        </div>
      )}
      {error && (
        <div className="p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
            {displayYear} Minor League Draft Picks
          </h3>
          <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
            {filteredPicks.length} pick{filteredPicks.length !== 1 ? 's' : ''}
            {selectedTeamId ? ' for selected team' : ' across all teams'}
          </p>
        </div>
        <button
          onClick={loadPicks}
          disabled={loading}
          className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-48 max-w-xs">
          <select
            value={selectedTeamId}
            onChange={e => setSelectedTeamId(e.target.value)}
            className={`${dynastyTheme.components.input} w-full`}
            disabled={loading}
          >
            <option value="">All Teams</option>
            {teams.map(team => (
              <option key={team.team_id} value={team.team_id}>
                {team.team_name}
              </option>
            ))}
          </select>
        </div>
        <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
          Filter by Team
        </div>
      </div>

      {/* Picks table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={`border-b ${dynastyTheme.classes.border.neutral}`}>
              <th className={`text-left py-2 px-3 text-xs font-medium ${dynastyTheme.classes.text.neutralLight}`}>Round</th>
              <th className={`text-left py-2 px-3 text-xs font-medium ${dynastyTheme.classes.text.neutralLight}`}>Pick</th>
              <th className={`text-left py-2 px-3 text-xs font-medium ${dynastyTheme.classes.text.neutralLight}`}>Slot (Original Owner)</th>
              <th className={`text-left py-2 px-3 text-xs font-medium ${dynastyTheme.classes.text.neutralLight}`}>Current Owner</th>
              <th className={`text-left py-2 px-3 text-xs font-medium ${dynastyTheme.classes.text.neutralLight}`}>Traded</th>
              <th className={`text-left py-2 px-3 text-xs font-medium ${dynastyTheme.classes.text.neutralLight}`}>Selected</th>
              {!loading && <th className={`text-left py-2 px-3 text-xs font-medium ${dynastyTheme.classes.text.neutralLight}`}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="py-12 text-center">
                  <div className="inline-block w-6 h-6 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>Loading picks...</p>
                </td>
              </tr>
            ) : filteredPicks.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-10 text-center">
                  <AlertCircle className={`w-8 h-8 mx-auto mb-2 ${dynastyTheme.classes.text.neutralDark}`} />
                  <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                    {allPicks.length === 0
                      ? 'No ML draft picks found for this season.'
                      : 'No picks match the selected team.'}
                  </p>
                </td>
              </tr>
            ) : (
              filteredPicks.map((pick, index) => (
                <tr key={index} className={`border-b ${dynastyTheme.classes.border.neutral} hover:bg-neutral-800/30`}>
                  <td className={`py-2 px-3 ${dynastyTheme.classes.text.white}`}>
                    {pick.round_number}
                  </td>
                  <td className={`py-2 px-3 ${dynastyTheme.classes.text.neutralLight}`}>
                    {pick.pick_in_round ?? '—'}
                  </td>
                  <td className={`py-2 px-3 ${dynastyTheme.classes.text.neutralLight}`}>
                    {pick.original_owner_name ?? pick.team_name ?? '—'}
                  </td>
                  <td className={`py-2 px-3 font-medium ${dynastyTheme.classes.text.white}`}>
                    {pick.current_owner_name ?? pick.team_name ?? '—'}
                  </td>
                  <td className="py-2 px-3">
                    {pick.is_traded ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 font-medium">
                        Traded
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400 border border-green-500/30 font-medium">
                        Original
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {pick.is_picked ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400 border border-green-500/30">
                        {/* TODO: PlayerLink — pick.picked_player_name is a flat string, no mlb_id available here */}
                        {pick.picked_player_name || 'Picked'}
                      </span>
                    ) : (
                      <span className={`text-xs ${dynastyTheme.classes.text.neutralLighter}`}>Available</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {!pick.is_picked && (
                      <button
                        onClick={() => openReassignModal(pick)}
                        className={`text-xs px-2 py-1 rounded font-medium transition-colors ${dynastyTheme.classes.text.primary} border border-yellow-500/30 hover:bg-yellow-500/10`}
                      >
                        <ArrowLeftRight className="w-3 h-3 mr-1 inline" />
                        Reassign
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Reassign modal */}
      {reassigning && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className={`${dynastyTheme.components.card.base} w-full max-w-md mx-4`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                  Reassign ML Pick
                </h3>
                <button onClick={closeReassignModal} className={`${dynastyTheme.classes.text.neutralLight} hover:text-white`}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className={`${dynastyTheme.classes.bg.darkLighter} rounded-lg p-4 mb-4 text-sm space-y-1`}>
                <div className={dynastyTheme.classes.text.neutralLight}>
                  <span className="font-medium">Round:</span>{' '}
                  <span className={dynastyTheme.classes.text.white}>{reassigning.round_number}</span>
                </div>
                {reassigning.pick_in_round && (
                  <div className={dynastyTheme.classes.text.neutralLight}>
                    <span className="font-medium">Pick in Round:</span>{' '}
                    <span className={dynastyTheme.classes.text.white}>{reassigning.pick_in_round}</span>
                  </div>
                )}
                <div className={dynastyTheme.classes.text.neutralLight}>
                  <span className="font-medium">Current Owner:</span>{' '}
                  <span className={dynastyTheme.classes.text.white}>{reassigning.current_owner_name ?? reassigning.team_name}</span>
                </div>
                <div className={dynastyTheme.classes.text.neutralLight}>
                  <span className="font-medium">Original Owner (Slot):</span>{' '}
                  <span className={dynastyTheme.classes.text.white}>{reassigning.original_owner_name ?? reassigning.team_name}</span>
                </div>
              </div>

              <label className={`${dynastyTheme.components.label} block mb-2`}>
                Assign to Team
              </label>
              <select
                value={targetTeamId}
                onChange={e => setTargetTeamId(e.target.value)}
                className={`${dynastyTheme.components.input} w-full mb-4`}
                disabled={reassigning.saving}
              >
                <option value="">— Select a team —</option>
                {teams
                  .filter(t => t.team_id !== (reassigning.current_owner_team_id ?? reassigning.team_id))
                  .map(team => (
                    <option key={team.team_id} value={team.team_id}>
                      {team.team_name}
                    </option>
                  ))}
              </select>

              <div className="flex gap-3">
                <button
                  onClick={handleReassign}
                  disabled={!targetTeamId || reassigning.saving}
                  className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
                >
                  {reassigning.saving ? 'Reassigning...' : 'Confirm Reassign'}
                </button>
                <button
                  onClick={closeReassignModal}
                  disabled={reassigning.saving}
                  className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MinorLeaguePicksOverride;
